<template>
  <div
    ref="tablebox"
    class="tablebox"
    :class="{ fullscreen: isFullscreen }"
    @scroll.passive="$emit('table-scroll', $event)"
  >
    <div class="fullscreen-actions">
      <button class="icon-btn" title="下载" @click.stop="downloadTable">
        <FaIcon :icon="faDownload" :size="16" />
      </button>
      <button class="icon-btn" title="退出全屏" @click.stop="toggleFullscreen">
        <FaIcon :icon="faCompress" :size="16" />
      </button>
    </div>
    <table>
      <thead>
        <tr>
          <th class="timecol">时间</th>
          <th
            v-for="(day, i) in days"
            :key="day.date"
            :class="{ 'is-today': i === todayIdx }"
          >
            {{ dayNames[i] }}
            <span v-if="i === todayIdx" class="today-badge">今天</span>
            <span class="daydate">{{ formatDayDate(day.date) }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(group, gIdx) in slotGroups">
          <tr
            v-if="group.type === 'collapsed'"
            :key="'collapsed-' + group.start + '-' + group.end"
            class="collapsed-row"
            @click="expandGroup(group.start, group.end)"
          >
            <td :colspan="8">
              <span class="fold-hint">
                <span class="fold-icon">▶</span>
                {{ collapsedRangeLabel(group.start, group.end) }}（已折叠，点击展开）
              </span>
            </td>
          </tr>
          <tr v-else :key="'row-' + group.index">
            <td class="timecell">
              <span
                v-if="isFoldable(group.index)"
                class="fold-btn"
                title="折叠该行"
                @click.stop="toggleCollapse(group.index)"
              >▼</span>
              <span>{{ slots[group.index] }}</span>
            </td>
            <td
              v-for="dIdx in 7"
              :key="dIdx - 1 + '-' + group.index"
              :class="['datacell', { 'is-today': dIdx - 1 === todayIdx }]"
            >
              <div class="cellinner">
                <div
                  v-for="(slice, idx) in renderItemsForCell(dIdx - 1, gIdx)"
                  :key="idx"
                  class="event-block"
                  :class="eventBlockClass(slice, dIdx - 1)"
                  :style="sliceStyle(slice)"
                  :title="hideEventTitle ? '' : sliceTitle(slice)"
                  @click="onEditClick(dIdx - 1, slice.eventIndex, slice.key, $event)"
                  @mouseenter="onBlockHoverEnter(dIdx - 1, slice.eventIndex, $event)"
                  @mouseleave="onBlockHoverLeave"
                >
                  <span v-if="!isMicroBlock(slice)" class="event-text">
                    <!-- 标题 + 事项 + 时间：分类标题常驻（优先级最高，见 fitEventText）；
                         只有带 fa/emoji 图标时才显示图标；没图标的不显示图标、也不显示色点。
                         无分类的记录（catName 为空）不渲染分类行，避免多出一个空行占高度 -->
                    <span
                      v-if="slice.catIconType === 'fa' || slice.catIconType === 'emoji'"
                      class="event-cat"
                    >
                      <i v-if="slice.catIconType === 'fa'" :class="'fas fa-' + slice.catIconValue"></i>
                      <span v-else class="event-cat-emoji">{{ slice.catIconValue }}</span>
                      <span class="event-cat-name">{{ slice.catName }}</span>
                    </span>
                    <span v-else-if="slice.catName" class="event-cat-name">{{ slice.catName }}</span>
                    <span v-if="slice.text" class="event-title">{{ slice.text }}</span>
                    <!-- 在做：结束时间还没发生，显示「xx:xx 起」而不是假的结束时间 -->
                    <span v-if="!isShortBlock(slice)" class="event-time">{{ blockTimeLabel(slice) }}</span>
                  </span>
                  <!--
                    状态徽标：在做 / 在做(记录) / 在做(计划) / 计划
                    记录、计划块只要当前时刻落在它自己的区间内，也提示「在做」，
                    但括号里标出真实类型 —— 块的底色 / 描边 / 按钮仍按实际 kind 渲染，
                    只有这枚徽标变成「正在进行」的强调色。
                  -->
                  <span
                    v-show="!isMicroBlock(slice) && blockStatusText(slice, dIdx - 1)"
                    class="block-status"
                    :class="blockStatusClass(slice, dIdx - 1)"
                  >{{ blockStatusText(slice, dIdx - 1) }}</span>
                  <!--
                    「在做」的「结束（暂停）」：**文字下方、整块垂直居中**，一枚大号纯图标按钮。
                    不放在右上角 .event-actions 里：那里是「删除 ×」这种次要操作的位置，
                    而「结束」是「在做」的头号动作，压在角上既小又容易和 × 混淆。
                    作为 flex 子项排在 .event-text 之后；`.event-block.is-running` 上加
                    `justify-content:center`，让「文字 + 按钮」这一组整体垂直居中，
                    按钮因此落在块的**竖向中间**而不是贴底。
                    图标走 Font Awesome（`fas fa-circle-pause`，圆圈里两根竖条），与块内分类图标同一套；
                    选「圆圈」形是因为按钮去掉了底色 —— 自带外圈才有一枚完整的图标轮廓。
                  -->
                  <button
                    v-if="!readonly && !isMicroBlock(slice) && slice.kind === 'running'"
                    type="button"
                    class="running-end"
                    title="结束并记录实际用时"
                    @click.stop="onEndRunning(dIdx - 1, slice.eventIndex, slice.key)"
                  >
                    <i class="fas fa-circle-pause"></i>
                  </button>
                  <!--
                    「计划」的「已完成」：**和「在做」的「结束」完全同一套呈现** —— 文字下方、整块垂直居中，
                    一枚大号纯图标按钮，不塞进右上角的 .event-actions（那里只留给「删除 ×」这类次要操作）。
                    「勾选完成」是「计划」块的头号动作，压在角上既小又容易和 × 混淆。
                    图标沿用 Font Awesome `fas fa-circle-check`（与 .event-actions 里那枚相同）。
                  -->
                  <button
                    v-if="!readonly && !isMicroBlock(slice) && slice.kind === 'planned'"
                    type="button"
                    class="planned-done"
                    title="勾选完成"
                    @click.stop="onCheckDone(dIdx - 1, slice.eventIndex, slice.key)"
                  >
                    <i class="fas fa-circle-check"></i>
                  </button>
                  <div v-show="!readonly && !isMicroBlock(slice)" class="event-actions" @click.stop>
                    <el-button
                      type="text"
                      title="删除"
                      @click.stop="onDeleteClick(dIdx - 1, slice.eventIndex, slice.key)"
                    >
                      <i class="fas fa-xmark"></i>
                    </el-button>
                  </div>
                  <!-- 互动角标：右下角显示该块的评论数 / 点赞数，方便一眼判断这里有没有好友互动。
                       极小块（is-micro，连文字都不显示）放不下角标，直接不渲染。 -->
                  <span
                    v-if="slice.badges && !isMicroBlock(slice)"
                    class="event-badges"
                  >
                    <span
                      v-if="slice.badges.commentCount > 0"
                      class="event-badge is-comment"
                      :title="slice.badges.commentCount + ' 条评论'"
                    >
                      <i class="fas fa-comment"></i>
                      <span class="event-badge-num">{{ slice.badges.commentCount }}</span>
                    </span>
                    <span
                      v-if="slice.badges.likeCount > 0"
                      class="event-badge is-like"
                      :title="slice.badges.likeCount + ' 个赞'"
                    >
                      <i class="fas fa-heart"></i>
                      <span class="event-badge-num">{{ slice.badges.likeCount }}</span>
                    </span>
                  </span>
                </div>
                <div
                  v-for="(gap, idx) in gapButtonsForCell(dIdx - 1, gIdx)"
                  v-show="!readonly"
                  :key="'gap-' + idx"
                  class="addbtn-gap"
                  :style="gapButtonStyle(gap)"
                  title="添加记录"
                  @click.stop="openModal(gap.dayIdx, gap.start, gap.end)"
                >
                  <span class="addbtn-icon">+</span>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <QuickAddModal
      ref="quickAddModal"
      :visible="modalVisible"
      :fullscreen="isFullscreen"
      :day-idx="modalDayIdx"
      :range-start="modalRangeStart"
      :range-end="modalRangeEnd"
      :last-category="lastCategory"
      :event-data="editingEvent"
      :days="days"
      :project-id="projectId"
      :friend-owner-id="friendOwnerId"
      @confirm="onModalConfirm"
      @cancel="closeModal"
      @delete="onModalDelete"
    />
  </div>
</template>

<script>
import { DAY_NAMES, SLOTS, DAY_MINUTES, minutesToLabel as fmtMinutes, categoryPalette, parseTagName, BLOCK_TEXT_COLOR, hexToRgba } from './constants'
import QuickAddModal from './QuickAddModal.vue'
import { deleteTimeBlock, updateTimeBlock, updateTimeBlockStatus } from '@/api/timeline.js'
import { deleteFriendTimeBlock, updateFriendTimeBlock, updateFriendTimeBlockStatus } from '@/api/share.js'
import { endRunningBlock, isoLocal, dateStrOf } from '@/utils/runningEnd.js'
import { toNumber } from '@/utils/number.js'
import { timerOf } from '@/utils/runningTimer.js'
import FaIcon from '@/components/common/FaIcon.vue'
// 图标一律走 Font Awesome 图标库（子路径引入），禁止手写 <svg> 画图标
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { faCompress } from '@fortawesome/free-solid-svg-icons/faCompress'

/**
 * 取时间块的屏幕坐标（viewport 坐标系，气泡用 position:fixed 定位）。
 * DOMRect 是「活」对象，滚动后会跟着变，这里拷成普通对象定格当前位置。
 */
function copyRect(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return null
  const r = el.getBoundingClientRect()
  return {
    top: r.top,
    left: r.left,
    right: r.right,
    bottom: r.bottom,
    width: r.width,
    height: r.height
  }
}

function todayString() {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** 当前时刻在当天的分钟坐标（0 ~ 1439），用于判定块是否「正在发生」 */
function currentMinute() {
  const d = new Date()
  return d.getHours() * 60 + d.getMinutes()
}

export default {
  name: 'TimeTable',
  components: { QuickAddModal, FaIcon },
  props: {
    days: {
      type: Array,
      required: true
    },
    events: {
      type: Array,
      default: () => [[], [], [], [], [], [], []]
    },
    foldableRows: {
      type: Set,
      default: () => new Set()
    },
    collapsedRows: {
      type: Set,
      default: () => new Set()
    },
    categories: {
      type: Array,
      default: () => []
    },
    projectId: {
      type: [String, Number],
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    },
    /** 好友只读表专用：隐藏原生 title，避免与点击打开的评论面板信息重复。 */
    hideEventTitle: {
      type: Boolean,
      default: false
    },
    /**
     * 好友协同编辑场景：非 null 时本表是被「授权编辑」的好友在改 ownerId 的时间表。
     * 此时删除/冲突缩短会走好友编辑接口（带权限校验 + 缓存失效），而不再走自己的接口。
     * 由 FriendTablePanel 在获得 EDIT 授权时传入好友的用户 ID。
     */
    friendOwnerId: {
      type: [String, Number],
      default: null
    },
    /**
     * 是否在「可编辑」表格上也抛 hover 事件（供所有者查看时间块评论）。
     * 默认 false：自己的表格 hover 不抛事件，行为不变；
     * 置 true 后 hover 进入时间块会 emit hover-event / hover-leave，供父组件弹评论气泡。
     * 只读表（好友页）本就抛 hover 事件，与它无关。
     */
    commentable: {
      type: Boolean,
      default: false
    },
    /**
     * 当前评论面板对应的时间块 —— `{ dayIdx, index }`（index 是该天 events 数组里的下标）。
     * 命中的块会挂上 `.is-bubble-source` 高亮，和居中的评论面板标题相互确认来源。
     * 只读的好友表（FriendTablePanel）才会传；自己的表格页不传，行为完全不变。
     *
     * ⚠️ 用 dayIdx+index 而不是 event.id：一个跨小时行的时间块会被切成多个 slice，
     *    它们共享同一个 eventIndex 但没有各自的 id，按 id 匹配会只高亮其中一段。
     */
    activeBlock: {
      type: Object,
      default: null
    },
    /**
     * 时间块的互动统计：`{ '<blockId>': { commentCount, likeCount } }`。
     *
     * <p>由表格页一次性批量拉取后传入，用于在时间块**右下角**画「评论 / 点赞 图标 + 数字」，
     * 让用户扫一眼就知道哪块有好友互动。没有互动的块不在这个映射里，因此不会渲染角标；
     * 不传（如好友页）则完全不渲染，行为与改动前一致。</p>
     *
     * <p>⚠️ 键统一按字符串索引：后端 Long 会被序列化成字符串，前端 JSONbig 解析出的 id 也是字符串。</p>
     */
    interactions: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dayNames: DAY_NAMES,
      slots: SLOTS,
      modalVisible: false,
      modalDayIdx: 0,
      modalRangeStart: 0,
      modalRangeEnd: DAY_MINUTES,
      lastCategory: 'none',
      editingEvent: null,
      isFullscreen: false,
      pendingPayload: null,
      todayKey: todayString(),
      /**
       * 「此刻」的分钟坐标。块是否「正在发生」全靠它判定 ——
       * 记录 / 计划块当前时刻落在自己区间内时，徽标也要提示「在做」，
       * 只是括号里标出真实类型。由 mounted 里的 60s 定时器推进。
       */
      nowMinute: currentMinute(),
      // Font Awesome 图标定义（模板 :icon 用）
      faDownload,
      faCompress
    }
  },
  computed: {
    todayIdx() {
      return this.days.findIndex(d => d.date === this.todayKey)
    },
    catMap() {
      return this.categories.reduce((map, cat) => {
        map[cat.key] = cat
        return map
      }, {})
    },
    slotGroups() {
      const groups = []
      let i = 0
      while (i < this.slots.length) {
        if (this.collapsedRows.has(i)) {
          let end = i
          while (end + 1 < this.slots.length && this.collapsedRows.has(end + 1)) {
            end++
          }
          groups.push({ type: 'collapsed', start: i, end })
          i = end + 1
        } else {
          groups.push({ type: 'row', index: i })
          i++
        }
      }
      return groups
    },
    dayRenderItems() {
      const items = []
      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        items.push(this.buildRenderItems(dayIdx))
      }
      return items
    },
    dayGapButtons() {
      const items = []
      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        items.push(this.computeDayGapButtons(dayIdx))
      }
      return items
    }
  },
  watch: {
    // 折叠/展开会改变实际渲染的行数，全屏时需要重新分配行高
    slotGroups() {
      this.$nextTick(this.syncFullscreenRowHeight)
    }
  },
  methods: {
    /**
     * 全屏时按「可视高度 ÷ 行数」反推行高（写进 --tt-row-h），让整张表正好铺满屏幕。
     * - 折叠行保持自己的紧凑高度，不参与均分；
     * - 剩余空间平均分给每个「小时行」，保证可见小时行之间仍然等距（时间刻度不被拉歪）；
     * - 退出全屏时移除该变量，自动回落到默认的 42px。
     */
    syncFullscreenRowHeight() {
      const box = this.$refs.tablebox
      if (!box) return
      if (!this.isFullscreen) {
        box.style.removeProperty('--tt-row-h')
        return
      }
      const table = box.querySelector('table')
      if (!table || !table.tBodies[0]) return
      const rows = Array.from(table.tBodies[0].rows)
      const normalRows = rows.filter(tr => !tr.classList.contains('collapsed-row'))
      if (!normalRows.length) return
      const collapsedH = rows
        .filter(tr => tr.classList.contains('collapsed-row'))
        .reduce((sum, tr) => sum + tr.offsetHeight, 0)
      const headerH = table.tHead ? table.tHead.getBoundingClientRect().height : 0
      // 4px = 表头顶边 2px + 末行底边 2px 的外框描边
      const avail = box.clientHeight - headerH - collapsedH - 4
      const h = Math.max(22, Math.floor(avail / normalRows.length))
      box.style.setProperty('--tt-row-h', h + 'px')
    },
    // 行高 42px：100% = 42px，50% = 21px（约 30 分钟），25% ≈ 10.5px（15 分钟），15% ≈ 6.3px（~9 分钟）
    // 块高 < 50% 时只能容纳一行文字，隐藏时间以避免挤掉标题；< 25% 时再缩小标题字号；
    // < 15% 时即使标题也几乎不可读，直接隐藏文字，只留色块，hover 看 tooltip
    isShortBlock(slice) {
      return slice.height < 50
    },
    isTinyBlock(slice) {
      return slice.height < 25
    },
    isMicroBlock(slice) {
      return slice.height < 15
    },
    eventBlockClass(slice, dayIdx) {
      const isMicro = this.isMicroBlock(slice)
      const active = this.activeBlock
      // 有 key 就按 key 认块：评论气泡弹出后若删过前面的记录，index 会指向别的块
      const isSource = !!active &&
        Number(active.dayIdx) === Number(dayIdx) &&
        (active.key != null && slice.key != null
          ? String(active.key) === String(slice.key)
          : Number(active.index) === Number(slice.eventIndex))
      return {
        // 互斥：micro 比 tiny 更小，命中 micro 时不再挂 tiny，避免样式叠加冲突
        'is-micro': isMicro,
        'is-tiny': !isMicro && this.isTinyBlock(slice),
        // 当前评论气泡指向的块（含被切分的每一段）
        'is-bubble-source': isSource,
        // 三种状态：done（普通）/ running（正在做）/ planned（预设）
        'is-running': slice.kind === 'running',
        'is-planned': slice.kind === 'planned'
      }
    },
    formatDayDate(dateStr) {
      const d = new Date(dateStr)
      return `${d.getMonth() + 1}/${d.getDate()}`
    },
    minutesToLabel(minutes) {
      return fmtMinutes(minutes)
    },
    /**
     * 时间块里那行小时间。
     * - 普通 / 预设：`（10:00-11:30）`
     * - 在做：`（10:00 起）` —— 结束时间还没发生，fullEnd 只是「填充到下一个时间块」的边界，
     *   显示出来会被误读成已经做到了那个点。
     * - 在做 + 设过定时：`（10:00 起 · 定时 11:30）` —— 否则刷新页面后用户看不出
     *   自己还挂着提醒，容易以为定时"莫名消失"。
     */
    blockTimeLabel(slice) {
      if (slice.kind === 'running') {
        const timer = slice.timerLabel ? ` · 定时 ${slice.timerLabel}` : ''
        return `（${fmtMinutes(slice.fullStart)} 起${timer}）`
      }
      return `（${fmtMinutes(slice.fullStart)}-${fmtMinutes(slice.fullEnd)}）`
    },
    /**
     * 这一块此刻是否「正在发生」：只有今天这一列才谈得上，
     * 且当前分钟严格落在块的开始与结束之间（左闭右开，与闭区间占用口径一致）。
     *
     * ⚠️ 只看时间区间，不看 kind —— 记录（done）和计划（planned）只要时间对得上，
     *    用户此刻就是「在这个块里」，徽标要提示在做。
     */
    isLiveNow(slice, dayIdx) {
      if (Number(dayIdx) !== this.todayIdx) return false
      const now = toNumber(this.nowMinute)
      return now > toNumber(slice.fullStart) && now < toNumber(slice.fullEnd)
    },
    /**
     * 状态徽标文案（空串 = 不显示徽标）：
     * - running（只有实际开始）→ 「在做」
     * - done / planned 且此刻在区间内 → 「在做(记录)」/「在做(计划)」—— 提示在做，但按实际类型标注
     * - planned 但还没到时间 → 「计划」
     * - done 且不在区间内 → 不显示
     */
    blockStatusText(slice, dayIdx) {
      if (slice.kind === 'running') return '在做'
      if (this.isLiveNow(slice, dayIdx)) {
        return slice.kind === 'planned' ? '在做(计划)' : '在做(记录)'
      }
      return slice.kind === 'planned' ? '计划' : ''
    },
    /**
     * 徽标配色：凡是「在做」都用同一套强调色（含记录 / 计划在区间内的情形），
     * 否则退回计划的中性色。块的底色 / 描边不受影响，仍按实际 kind 渲染。
     */
    blockStatusClass(slice, dayIdx) {
      if (slice.kind === 'running' || this.isLiveNow(slice, dayIdx)) return 'is-running'
      return 'is-planned'
    },
    rowMinutes(idx) {
      return idx * 60
    },
    renderItemsForCell(dayIdx, groupIdx) {
      return (this.dayRenderItems[dayIdx] || []).filter(item => item.groupIndex === groupIdx)
    },
    gapButtonsForCell(dayIdx, groupIdx) {
      return (this.dayGapButtons[dayIdx] || []).filter(item => item.groupIndex === groupIdx)
    },
    /**
     * 计算某天的空档（"+"按钮）。
     *
     * ⚠️ 时间区间按**闭区间**理解：块显示成「16:00-16:40」时，**16:40 这一分钟已经被它占用**，
     *    所以紧接着的下一块最早只能从 16:41 开始，不能从 16:40 开始。
     *    由此空档的边界是：
     *    - 起点 = 前一块结束时间 **+ 1 分钟**（16:40 → 16:41）
     *    - 终点 = 后一块开始时间 **- 1 分钟**（后一块 17:00 开始 → 最晚填到 16:59）
     *    这样相邻两块永远不会共用同一分钟，也不会在中间留下插不进内容的缝。
     */
    computeDayGapButtons(dayIdx) {
      const dayEvents = this.events[dayIdx] || []
      const gaps = []
      if (dayEvents.length === 0) {
        gaps.push({ start: 0, end: DAY_MINUTES })
      } else {
        const sorted = [...dayEvents].sort((a, b) => a.start - b.start)
        const occupied = []
        let current = null
        sorted.forEach(evt => {
          if (!current) {
            current = { start: evt.start, end: evt.end }
          } else if (evt.start <= current.end + 1) {
            // 相差 1 分钟即视为连续（中间插不进任何记录），合并成同一段占用
            current.end = Math.max(current.end, evt.end)
          } else {
            occupied.push(current)
            current = { start: evt.start, end: evt.end }
          }
        })
        if (current) occupied.push(current)
        // prevEnd = 上一个占用区间的结束分钟；-1 表示它前面还没有任何记录
        let prevEnd = -1
        occupied.forEach(interval => {
          const gapStart = prevEnd + 1
          const gapEnd = interval.start - 1
          if (gapEnd >= gapStart) {
            gaps.push({ start: gapStart, end: gapEnd })
          }
          prevEnd = Math.max(prevEnd, interval.end)
        })
        // 末尾空档：终点仍可以是 24:00 —— 当天最后一分钟之后就是次日，不存在"被下一块占用"
        if (prevEnd + 1 < DAY_MINUTES) {
          gaps.push({ start: prevEnd + 1, end: DAY_MINUTES })
        }
      }
      const segments = []
      gaps.forEach(gap => {
        segments.push(...this.splitGapIntoSegments(dayIdx, gap.start, gap.end))
      })
      return segments
    },
    splitRangeByGroups(rangeStart, rangeEnd) {
      const segments = []
      let currentSeg = null
      this.slotGroups.forEach((group, gIdx) => {
        if (group.type === 'collapsed') {
          if (currentSeg) {
            segments.push({ ...currentSeg })
            currentSeg = null
          }
          return
        }
        const rowIdx = group.index
        const rowStart = this.rowMinutes(rowIdx)
        const rowEnd = rowStart + 60
        const s = Math.max(rangeStart, rowStart)
        const e = Math.min(rangeEnd, rowEnd)
        if (e > s) {
          if (currentSeg && currentSeg.lastGIdx + 1 === gIdx) {
            currentSeg.end = e
            currentSeg.rowCount++
            currentSeg.lastGIdx = gIdx
          } else {
            if (currentSeg) {
              segments.push({ ...currentSeg })
            }
            currentSeg = { rowIdx, gIdx, start: s, end: e, rowCount: 1, lastGIdx: gIdx }
          }
        } else if (currentSeg) {
          segments.push({ ...currentSeg })
          currentSeg = null
        }
      })
      if (currentSeg) {
        segments.push({ ...currentSeg })
      }
      return segments
    },
    splitGapIntoSegments(dayIdx, gapStart, gapEnd) {
      return this.splitRangeByGroups(gapStart, gapEnd).map(seg => this.makeGapButtonItem(seg, dayIdx))
    },
    makeBaseItem(seg, collapseTop = false) {
      const rowStart = this.rowMinutes(seg.rowIdx)
      // 闭区间相邻：上一块结束于 16:00、下一块开始于 16:01 时，两段数据上首尾相接（中间没有空分钟），
      // 但按「start - rowStart」定位，下一块会空出约 1.67% 行高（/ 行边界处约 0.7px）的可见缝隙。
      // collapseTop 只在「块的第一段」为真：把渲染起点提前 1 分钟、高度加 1 分钟，
      // 让两块严丝合缝贴在一起。注意只动 top/height，evt.start/end 真实值一律不动，
      // 冲突检测、空档按钮、tooltip 时间都按真实值算。
      const renderStart = collapseTop ? seg.start - 1 : seg.start
      return {
        groupIndex: seg.gIdx,
        start: seg.start,
        end: seg.end,
        top: ((renderStart - rowStart) / 60) * 100,
        height: ((seg.end - renderStart) / 60) * 100,
        spanRows: seg.rowCount
      }
    },
    makeGapButtonItem(seg, dayIdx) {
      return {
        ...this.makeBaseItem(seg),
        dayIdx
      }
    },
    gapButtonStyle(gap) {
      return {
        top: `${gap.top}%`,
        height: `${gap.height}%`
      }
    },
    buildRenderItems(dayIdx) {
      const dayEvents = this.events[dayIdx] || []
      const items = []
      // 按开始时间排序后逐块判定「是否与上一块闭区间相邻」：
      // 上一块结束于 prevEnd、本块从 prevEnd+1 开始 → 两段之间没有任何空分钟，渲染时要贴紧。
      const sorted = [...dayEvents].sort((a, b) => a.start - b.start)
      let prevEnd = -1
      sorted.forEach((evt, eventIndex) => {
        const adjacent = prevEnd >= 0 && evt.start === prevEnd + 1
        const segments = this.splitRangeByGroups(evt.start, evt.end)
        segments.forEach((seg, segIdx) => {
          // 只有「最后一段」挂互动角标：跨小时行的时间块会被切成多段，
          // 每段都挂会在同一个块上重复出现好几个角标，而右下角本该只有一个。
          // 只有「第一段」参与闭区间贴紧（collapseTop），避免把后续段也往上拽。
          items.push(this.makeItem(seg, eventIndex, evt, segIdx === segments.length - 1, segIdx === 0 && adjacent))
        })
        prevEnd = Math.max(prevEnd, evt.end)
      })
      return items
    },
    makeItem(seg, eventIndex, evt, isLastSeg = true, collapseTop = false) {
      const item = {
        ...this.makeBaseItem(seg, collapseTop),
        eventIndex,
        // 块的稳定标识：点击/删除/冲突处理都按它定位，不用数组下标（见 isSameBlock 注释）
        key: evt.key,
        cat: evt.cat,
        text: evt.text,
        color: evt.color,
        // 时间块类型：done（普通）/ running（正在做）/ planned（预设）
        kind: evt.kind || 'done',
        fullStart: evt.start,
        fullEnd: evt.end
      }
      // 「在做」+ 设过定时：把提醒时刻带到渲染层，块上要显示「定时 11:30」，
      // 否则用户刷新页面后完全不知道自己还挂着提醒（弹窗没开时会显得莫名消失）。
      // ⚠️ 按后端 id 找（不是 evt.key，那是每次加载重生成的），且 timerOf 读的是
      //    Vue.observable 的 timers 对象 → 增删定时会自动让这里的 computed 重算。
      if (item.kind === 'running' && evt.id) {
        const timer = timerOf(evt.id)
        if (timer) item.timerLabel = timer.label || ''
      }
      // 预解析分类的图标 + 名称，供时间块展示「分类图标 + 名称」
      const cat = evt.cat && evt.cat !== 'none' ? this.catMap[evt.cat] : null
      if (cat) {
        const parsed = parseTagName(cat.label)
        item.catIconType = parsed.iconType
        item.catIconValue = parsed.iconValue
        item.catName = parsed.text
        item.catColor = cat.color
      }
      // 互动角标：只在真的有点赞或评论时才挂，模板据此决定要不要渲染右下角
      if (isLastSeg && evt.id) {
        const summary = this.interactions[String(evt.id)]
        if (summary) {
          // 后端 Long 到前端是字符串，必须过 toNumber 才能参与比较（否则 "0" > 0 为 false 尚可，
          // 但 "0" !== 0 之类的严格比较会出错，统一转数字最稳）
          const commentCount = toNumber(summary.commentCount)
          const likeCount = toNumber(summary.likeCount)
          if (commentCount > 0 || likeCount > 0) {
            item.badges = { commentCount, likeCount }
          }
        }
      }
      return item
    },
    /**
     * 时间块配色，按 kind 区分三种状态：
     *
     * - **done（普通）**：底色用 `categoryPalette(...).blockBg`（色点原色混入 12% 白），
     *   描边用色点原色 `base`，与图例色点一致。
     * - **running（正在做）**：渐变底色——开始时间处颜色最深，向下逐渐变淡（用户要求）。
     *   渐变方向 `to bottom` 正好对应「时间从上往下走」，视觉上「越往后越淡」= 进行中。
     * - **planned（预设）**：虚线描边 + 极浅底色，明显区别于已确定的实线块。
     *
     * 文字一律用固定的黑灰 `BLOCK_TEXT_COLOR`（= 图例标签文字色），不随分类色变化。
     */
    sliceStyle(slice) {
      const style = {
        top: `${slice.top}%`,
        height: `${slice.height}%`
      }
      const cat = this.catMap[slice.cat]
      const kind = slice.kind || 'done'
      if (cat) {
        const p = categoryPalette(cat.color, cat.label)
        if (kind === 'running') {
          // 正在做：顶部最深（base 原色）→ 底部淡出（同色 30% 透明度兑在白底上）
          style.background = this.runningGradient(p.base, slice)
          style.borderColor = p.base
          style.color = BLOCK_TEXT_COLOR
        } else if (kind === 'planned') {
          // 预设：极浅底 + 虚线描边
          style.background = hexToRgba(p.base, 0.12)
          style.border = `2px dashed ${p.base}`
          style.color = p.text
        } else {
          style.background = p.blockBg
          style.borderColor = p.base
          style.color = BLOCK_TEXT_COLOR
        }
      } else {
        // 没归类的记录：三种状态都用中性灰，但 running 走渐变、planned 走虚线
        if (kind === 'running') {
          style.background = this.runningGradient('#94a3b8', slice)
          style.borderColor = '#94a3b8'
        } else if (kind === 'planned') {
          style.background = 'rgba(148,163,184,0.12)'
          style.border = '2px dashed #94a3b8'
        } else {
          style.background = slice.color || '#e2e8f0'
        }
        style.color = slice.color ? BLOCK_TEXT_COLOR : '#4a5568'
      }
      return style
    },
    /**
     * 「在做」块的渐变底色：**整块**从开始（原色）到结束（同色 30% 透明）由深到浅。
     *
     * ⚠️ 跨小时行的块会被切成多个 slice。若每段都各画一遍 `0% → 100%` 的完整渐变，
     *    渐变会在每个整点处**重启**，看着就是一条条断开的色带。
     *    所以这里按「这一段在整块里的位置」换算出它两端的实际透明度：
     *    上一行结束时是什么色，下一行开头就从什么色接上，跨行后视觉完全连续。
     *
     * @param {string} base 分类原色（hex）
     * @param {Object} slice 当前切片（start/end 是本段，fullStart/fullEnd 是整块）
     */
    runningGradient(base, slice) {
      const span = Math.max(1, toNumber(slice.fullEnd) - toNumber(slice.fullStart))
      // 整块渐变是 alpha 1 → 0.3 的线性插值，取任意分钟处的 alpha：
      const alphaAt = min => 1 - 0.7 * Math.min(1, Math.max(0, (toNumber(min) - toNumber(slice.fullStart)) / span))
      const a0 = alphaAt(slice.start)
      const a1 = alphaAt(slice.end)
      if (Math.abs(a1 - a0) < 0.005) return hexToRgba(base, a0)
      return `linear-gradient(180deg, ${hexToRgba(base, a0)} 0%, ${hexToRgba(base, a1)} 100%)`
    },
    /**
     * 时间块文字排版：分类优先、从顶部排、放不下的「折叠」到下方、块内滚动查看。
     *
     * 用户口径：内边距再小一些；优先显示最上面的一行（分类）；显示不完的折叠起来，
     * 滑动事件块即可查看。所以这里**不再用 display:none 丢掉内容**：
     * - **分类行**（`.event-cat` / 无图标时的 `.event-cat-name`）优先级最高：
     *   自然字号放不下时先退到紧凑字号（`.is-cat-compact`），仍放不下才不显示；
     * - **纵向放不下的行**：整行下移出可视区（`.is-scroll` + 首个放不下的行加 `margin-top`
     *   顶到折叠线），块内可上下滑动完整查看 —— 既不会出现「被切一半的字」，
     *   内容也不会丢。剩余空间**对半分**垫在可见内容上下，块里不会留出多余的空白条；
     * - **横向放不下的行**（事项 / 时间）：整行隐藏，不留省略号
     *   （横向截断的文本靠上下滑动也看不全，留着只会显示成「背单词…」）。
     *
     * 纯 CSS 做不到这些，只能量。测量统一放在 `updated()` / ResizeObserver 之后，
     * 且**先全部读、再统一写**（`plans`），避免边读边写触发多次强制重排。
     */
    fitEventText() {
      const box = this.$refs.tablebox
      if (!box) return
      const plans = []
      box.querySelectorAll('.event-block').forEach(block => {
        const inner = block.querySelector('.event-text')
        if (!inner) return
        const lines = Array.from(inner.children)
        if (!lines.length) return
        // 复位上一轮的结果，保证每轮都按自然尺寸重新量
        block.classList.remove('is-cat-compact')
        inner.classList.remove('is-scroll')
        lines.forEach(el => {
          el.classList.remove('is-overflow')
          el.style.marginTop = ''
        })

        const cs = getComputedStyle(inner)
        // 极小块是「分类 + 事项」横排一行，纵向只比最高的那一行，不做高度累加
        const isRow = cs.flexDirection === 'row'
        const gap = parseFloat(cs.rowGap || cs.gap || '0') || 0
        // 留 0.5px 容差，避免亚像素舍入把「刚好放得下」的行误判成放不下
        const avail = inner.clientHeight + 0.5
        const hide = new Set()

        // 行的「自然高度」不能用 offsetHeight：横排时 flex 会把它拉伸成容器高度
        const natH = el => {
          const s = getComputedStyle(el)
          const fs = parseFloat(s.fontSize) || 0
          const lh = s.lineHeight === 'normal' ? fs * 1.2 : parseFloat(s.lineHeight) || fs * 1.2
          return Math.max(el.offsetHeight, lh)
        }

        const catEl = lines.find(el => el.classList.contains('event-cat') || el.classList.contains('event-cat-name'))
        // 分类行放不下时先退到紧凑字号，紧凑字号仍放不下才不显示
        if (catEl && natH(catEl) > avail) {
          block.classList.add('is-cat-compact')
          if (natH(catEl) > avail) hide.add(catEl)
        }
        // 连最上面那一行都放不下（块太矮，例如 15 分钟）：整体不显示文字，只留色条。
        // 不能只藏分类行 —— 那样会出现「事项还在、分类没了」的怪状态。
        if (lines[0] && hide.has(lines[0])) lines.forEach(el => hide.add(el))

        if (isRow) {
          // 横排：几行共用一个行高，最高的那行放不下就等于全都放不下
          const tallest = lines.reduce((m, el) => Math.max(m, natH(el)), 0)
          if (tallest > avail) lines.forEach(el => hide.add(el))
        } else {
          // 竖排：从顶部按优先级累加，第一个放不下的行连同其后的内容一起折到可视区外。
          //
          // ⚠️ 折下去的行必须「整行」推到可视区之外（不能留半行），否则会出现被切一半的字。
          //    做法：给「第一个放不下的行」加一段上边距，把它顶到容器下边缘之外。
          //    但这段边距不能全堆在底部 —— 那样块底会留下一条醒目的「空行」。
          //    所以把剩余空间**对半分**：一半垫在可见内容上方、一半当折叠行的上边距，
          //    可见内容因此垂直居中，块内看不到任何多余的空白条。
          let visibleH = 0
          let foldIndex = -1
          lines.forEach((el, i) => {
            if (hide.has(el) || foldIndex >= 0) return
            const need = i > 0 ? gap : 0
            if (visibleH + need + natH(el) > avail) {
              foldIndex = i
              return
            }
            visibleH += need + natH(el)
          })
          if (foldIndex >= 0) {
            const leftover = Math.max(0, avail - visibleH)
            const firstVisible = lines.find(el => !hide.has(el))
            if (firstVisible) firstVisible.style.marginTop = Math.floor(leftover / 2) + 'px'
            lines[foldIndex].style.marginTop = Math.ceil(leftover / 2) + 'px'
            inner.classList.add('is-scroll')
          }
        }

        lines.forEach(el => {
          if (hide.has(el)) return
          // 分类行最高优先级：横向放不下也不隐藏（真放不下时由 CSS 省略号兜底）
          if (el.classList.contains('event-cat') || el.classList.contains('event-cat-name')) return
          if (el.scrollWidth > el.clientWidth + 1) hide.add(el)
        })

        if (hide.size) plans.push([...hide])
      })
      plans.forEach(hide => hide.forEach(el => el.classList.add('is-overflow')))
    },
    /**
     * 悬浮提示文案：`时间段 分类名 事项`。
     *
     * ⚠️ 分类名要用 makeItem 里已经解析好的 `catName`（纯名称），
     *    不能直接用 `catMap[slice.cat].label` —— 那一整串里带着图标前缀
     *    （'fa:book学习'），而原生 title 只能显示纯文本，会把 'fa:book' 当成文字露出来。
     *    悬浮提示只承担「这块是什么」的补充，图标信息在色块里已经画出来了，这里不必重复。
     */
    sliceTitle(slice) {
      const time = slice.kind === 'running'
        ? `${fmtMinutes(slice.fullStart)} 起，进行中${slice.timerLabel ? `，定时 ${slice.timerLabel}` : ''}`
        : `${fmtMinutes(slice.fullStart)}-${fmtMinutes(slice.fullEnd)}`
      const catLabel = slice.catName || ''
      const text = slice.text || ''
      return [time, catLabel, text].filter(s => s).join(' ')
    },
    isFoldable(idx) {
      return this.foldableRows.has(idx)
    },
    toggleCollapse(idx) {
      let start = idx
      while (start > 0 && this.foldableRows.has(start - 1)) {
        start--
      }
      let end = idx
      while (end + 1 < this.slots.length && this.foldableRows.has(end + 1)) {
        end++
      }
      for (let i = start; i <= end; i++) {
        this.$emit('toggle-collapse', i)
      }
    },
    expandGroup(start, end) {
      for (let i = start; i <= end; i++) {
        this.$emit('toggle-collapse', i)
      }
    },
    collapsedRangeLabel(start, end) {
      const startTime = this.slots[start].split('-')[0]
      const endTime = this.slots[end].split('-')[1]
      return `${startTime}-${endTime}`
    },
    openModal(dayIdx, rangeStart, rangeEnd) {
      if (this.readonly) return
      this.editingEvent = null
      this.modalDayIdx = dayIdx
      this.modalRangeStart = rangeStart
      this.modalRangeEnd = rangeEnd
      this.modalVisible = true
    },
    closeModal() {
      this.modalVisible = false
      this.editingEvent = null
    },
    /**
     * 「修改记录」弹窗里的「删除」：先把弹窗收起来，再走时间块右上角 × 那条删除流程
     * （onDeleteClick 内部已经有「是否删除」二次确认 + 调接口 + emit delete-event）。
     */
    onModalDelete() {
      const editing = this.editingEvent
      if (!editing || typeof editing.index !== 'number') return
      const dayIdx = this.modalDayIdx
      const index = editing.index
      // ⚠️ closeModal() 会清掉 editingEvent，key 必须先取出来
      const key = editing.key
      this.closeModal()
      this.onDeleteClick(dayIdx, index, key)
    },
    onModalConfirm({ dayIdx, start, end, cat, text, index, key, id, kind, timerAt, plannedStartTime, plannedEndTime, actualStartTime, actualEndTime }) {
      // ⚠️ `id` 必须原样带上，不能只解构出 index/key：
      //    「新增记录」时 QuickAddModal 已经把后端返回的 id 放进了 payload（见其 confirm()），
      //    这里重建 payload 若把它丢掉，新块在本地就永远没有 id ——
      //    之后编辑/删除它时，QuickAddModal.updateRecord / TimeTable.onDeleteClick 都因为
      //    拿不到 id 而只改本地、不打后端（且不报错），刷新后自然"改完又变回去"。
      // ⚠️ `kind` 同样必须带上：普通 / 正在做 / 预设 三种状态由 QuickAddModal 选定，
      //    丢了会让新建的「正在做 / 预设」块在本地退化成普通块（渲染层按 kind 区分样式）。
      // ⚠️ `planned*/actual*` 四个原始时间戳、以及 `timerAt`（在做的定时提醒时刻，当天分钟坐标）
      //    也必须原样透传：本地新增的块要靠它们重新派生 start/end/kind（少 actualStart 会让
      //    之后点「结束」时派生失败、整块塌成 0-0），定时则要靠 timerAt 才能在 ListPage 起定时器。
      const payload = { dayIdx, start, end, cat, text, index, key, id, kind, timerAt, plannedStartTime, plannedEndTime, actualStartTime, actualEndTime }
      const conflicts = this.findConflicts(payload)
      // 好友协同编辑场景：时间冲突直接提示用户调整，不做自动缩短/删除，
      // 避免与父组件的持久化路径重复发请求。
      if (this.friendOwnerId && conflicts.length) {
        this.$message.warning('所选时间段与已有记录冲突，请调整后再添加')
        return
      }
      if (conflicts.length === 0) {
        this.lastCategory = cat
        this.emitPayload(payload)
        this.modalVisible = false
        this.editingEvent = null
        return
      }
      this.pendingPayload = payload
      // 先把「不冲突的可行时间」算出来：既写进提示里让用户有预期，也用于「调整新任务」时回填
      const suggestion = this.suggestNonConflictRange(payload)
      const message = this.buildConflictMessage(conflicts, payload, suggestion)
      this.$confirm(message, '时间冲突', {
        dangerouslyUseHTMLString: true,
        showCancelButton: true,
        confirmButtonText: '缩短旧任务',
        cancelButtonText: '调整新任务',
        type: 'warning'
      }).then(async () => {
        this.lastCategory = cat
        await this.resolveConflicts(payload, conflicts)
        this.emitPayload(payload)
        this.pendingPayload = null
        this.modalVisible = false
        this.editingEvent = null
      }).catch(() => {
        // 新增记录：弹窗里点「保存」的瞬间 QuickAddModal 就已经把它写进后端了
        // （见 QuickAddModal.confirm —— 先 createRecord 再 emit）。
        // 用户这时选「调整新任务」= 要换个时间，而弹窗之后会用新时间**再建一条**，
        // 刚才那条就成了没人认领的孤儿行（界面上没有、刷新也回不来）→ 先撤掉。
        this.discardPreCreatedBlock(payload)
        this.reopenModalWithSuggestion(payload, suggestion)
        this.pendingPayload = null
      })
    },
    /**
     * 撤掉「先落库再判冲突」留下来的那条记录。
     *
     * <p>只对**新增**生效：编辑走的是 update，弹窗重开后再次保存会用新时间覆盖，不会留孤儿。</p>
     */
    discardPreCreatedBlock(payload) {
      if (!payload.id) return
      // index / key 有值说明是编辑已有记录，不是新增
      if (payload.index != null || payload.key) return
      const req = this.friendOwnerId
        ? deleteFriendTimeBlock(this.friendOwnerId, payload.id)
        : deleteTimeBlock(payload.id)
      // 删不掉也只是多一条孤儿行，不能因此打断用户改时间
      req.catch(() => {})
    },
    /**
     * 「调整新任务」：重开（实际是复用已打开的）「修改记录」弹窗，并把时间改成算好的不冲突区间。
     *
     * ⚠️ 弹窗在冲突确认框弹出期间一直是 visible=true，watch(visible) 不会再触发、
     *    resetFields() 也不会重跑 —— 光改 editingEvent 不会让滑块动，必须显式调
     *    QuickAddModal.applyTimeRange() 把时间写进去。
     */
    reopenModalWithSuggestion(payload, suggestion) {
      this.modalVisible = true
      this.modalDayIdx = payload.dayIdx
      const i = this.findEventIndex(payload.dayIdx, payload.index, payload.key)
      if (i >= 0) {
        const evt = this.events[payload.dayIdx][i]
        this.editingEvent = { ...evt, index: i, key: evt.key }
      } else {
        this.editingEvent = null
      }
      if (!suggestion) return
      // 新增记录时弹窗的可选范围是点击的那个空档，建议时间可能落在它之外 → 先把范围放宽
      this.modalRangeStart = Math.min(this.modalRangeStart, suggestion.start)
      this.modalRangeEnd = Math.max(this.modalRangeEnd, suggestion.end)
      // 范围 props 要等一次渲染才生效，sliderMin/sliderMax 也要跟着更新后才能安全夹取
      this.$nextTick(() => {
        const modal = this.$refs.quickAddModal
        if (modal && typeof modal.applyTimeRange === 'function') {
          modal.applyTimeRange(suggestion.start, suggestion.end)
        }
        this.$message.info(
          `已避开冲突，时间调整为 ${fmtMinutes(suggestion.start)}-${fmtMinutes(suggestion.end)}`
        )
      })
    },
    /**
     * 给冲突的新任务算一段**不冲突**的时间。
     *
     * <p>规则（对照用户看到的结果）：</p>
     * <ol>
     *   <li>**先保住开始时间**：只要 start 那一分钟是空的，就把结束时间往前收到所在空档的末尾 ——
     *       例：新任务 12:01-14:32 撞上 12:58-15:28 → 建议 12:01-12:57。</li>
     *   <li>start 自己就被占用时，整块挪到最近的空档（先看后面、再看前面），并尽量保持原时长。</li>
     * </ol>
     *
     * ⚠️ 空档边界沿用闭区间约定：`上一块 end + 1` 起、`下一块 start - 1` 止（见 computeDayGapButtons）；
     *    参与计算的「占用」要排除自己（改记录时不能把自己算成冲突方）。
     *
     * @param {Object} payload onModalConfirm 收到的 `{ dayIdx, start, end, index, key }`
     * @returns {{start:number,end:number}|null} 整天排满、找不到空档时返回 null
     */
    suggestNonConflictRange(payload) {
      const dayEvents = (this.events[payload.dayIdx] || [])
        .filter(evt => !this.isSameBlock(evt, payload))
      const occupied = []
      dayEvents
        .map(evt => ({ start: toNumber(evt.start), end: toNumber(evt.end) }))
        .filter(b => Number.isFinite(b.start) && Number.isFinite(b.end) && b.end >= b.start)
        .sort((a, b) => a.start - b.start)
        .forEach(b => {
          const last = occupied[occupied.length - 1]
          // 相差 1 分钟即视为连续（中间插不进任何记录），合并成同一段占用
          if (last && b.start <= last.end + 1) last.end = Math.max(last.end, b.end)
          else occupied.push({ start: b.start, end: b.end })
        })
      const gaps = []
      let prevEnd = -1
      occupied.forEach(interval => {
        const gapStart = prevEnd + 1
        const gapEnd = interval.start - 1
        if (gapEnd >= gapStart) gaps.push({ start: gapStart, end: gapEnd })
        prevEnd = Math.max(prevEnd, interval.end)
      })
      // 末尾空档：终点仍可以是 24:00（当天最后一分钟之后就是次日，不存在"被下一块占用"）
      if (prevEnd + 1 < DAY_MINUTES) gaps.push({ start: prevEnd + 1, end: DAY_MINUTES })
      if (!gaps.length) return null

      const start = payload.start
      const span = Math.max(0, payload.end - payload.start)
      // ① 保住开始时间：start 落在哪个空档里，就把结束时间收到那个空档的末尾
      const keepStart = gaps.find(g => start >= g.start && start <= g.end)
      if (keepStart) return { start, end: Math.min(payload.end, keepStart.end) }
      // ② start 被占用：整块往后挪到最近的空档，尽量保持原时长
      const after = gaps.find(g => g.start > start)
      if (after) return { start: after.start, end: Math.min(after.start + span, after.end) }
      // 后面没空档了（start 在当天最后一段占用里）→ 贴到最后一段空档的末尾
      const before = gaps[gaps.length - 1]
      return { start: Math.max(before.start, before.end - span), end: before.end }
    },
    /**
     * 按下标定位某天的一条记录。
     *
     * ⚠️ **优先按 key 找，下标只作兜底**：冲突处理会先删/改其它记录，
     *    数组长度一变，之前记下来的 index 就指向别的块了（见 nextBlockKey 的说明）。
     */
    findEventIndex(dayIdx, index, key) {
      const dayEvents = this.events[dayIdx] || []
      if (key != null && key !== '') {
        const i = dayEvents.findIndex(evt => evt && String(evt.key) === String(key))
        if (i >= 0) return i
      }
      if (typeof index === 'number' && index >= 0 && index < dayEvents.length) return index
      return -1
    },
    /** 判断「已有记录」与「弹窗提交的这一条」是不是同一条（优先比 key，见上）。 */
    isSameBlock(item, payload) {
      if (payload.key != null && item.key != null) return String(item.key) === String(payload.key)
      return typeof payload.index === 'number' && item.index === payload.index
    },
    /**
     * 找出与新记录**重叠**的已有记录。
     *
     * ⚠️ 区间按闭区间判重叠（`start <= 对方 end && end >= 对方 start`）：
     *    结束时间那一分钟也属于这一块，所以「上一块到 16:40、下一块从 16:40 开始」
     *    其实共用了 16:40，同样算冲突 —— 下一块必须让到 16:41。
     *    用半开区间（`start < 对方 end`）会让这种"贴边"漏判，和空档的 +1 规则对不上。
     */
    findConflicts(payload) {
      const dayEvents = this.events[payload.dayIdx] || []
      return dayEvents
        .map((evt, idx) => ({ ...evt, index: idx }))
        .filter(item => !this.isSameBlock(item, payload) &&
          payload.start <= item.end && payload.end >= item.start)
    },
    /**
     * 冲突提示文案。
     * @param {Array} conflicts 冲突的旧记录
     * @param {Object} payload 新任务
     * @param {{start:number,end:number}|null} suggestion 算好的不冲突时间（见 suggestNonConflictRange）
     */
    buildConflictMessage(conflicts, payload, suggestion) {
      // 「在做」只填了开始时间，结束点是系统推出的「填充到下一个时间块」——
      //  显示成 "14:30-24:00" 会被误读成"已经做到了 24:00"，这里改成 "14:30 起"。
      const isRunning = payload.kind === 'running'
      const newTime = isRunning
        ? `${fmtMinutes(payload.start)} 起（进行中）`
        : `${fmtMinutes(payload.start)}-${fmtMinutes(payload.end)}`
      let html = `<div style="line-height:1.6;"><p>新${isRunning ? '的「在做」' : '任务'} <strong>${newTime}</strong> 与以下记录冲突：</p>`
      html += '<ul style="margin:8px 0;padding-left:20px;">'
      conflicts.forEach(conflict => {
        const cat = this.catMap[conflict.cat]
        // 同样要剥掉图标前缀：这是弹给用户的纯文本提示，'fa:book学习' 这种原始串不能出现
        const catLabel = cat ? (parseTagName(cat.label).text || cat.label) : (conflict.cat || '无分类')
        const time = `${fmtMinutes(conflict.start)}-${fmtMinutes(conflict.end)}`
        const text = conflict.text || '无事项'
        html += `<li>${time} · ${catLabel} · ${text}</li>`
      })
      html += '</ul><p>选择「缩短旧任务」会把重叠的部分从旧任务里去掉（旧任务结束时间提前到新任务开始的前一分钟，或开始时间推后到新任务结束的后一分钟）；'
      if (suggestion) {
        // 「在做」只有开始时间可调，这里也就只提开始时间，别再抛一个用户没填过的结束时间
        const freeTime = isRunning
          ? `${fmtMinutes(suggestion.start)} 起`
          : `${fmtMinutes(suggestion.start)}-${fmtMinutes(suggestion.end)}`
        html += `选择「调整新任务」会自动把时间调整到不冲突的 <strong>${freeTime}</strong>，可再微调。</p></div>`
      } else {
        html += '选择「调整新任务」可重新编辑时间。</p></div>'
      }
      return html
    },
    async resolveConflicts(payload, conflicts) {
      const sorted = [...conflicts].sort((a, b) => b.index - a.index)
      for (const conflict of sorted) {
        if (payload.start <= conflict.start && payload.end >= conflict.end) {
          // 新任务完全覆盖旧任务：删除旧任务
          await this.deleteConflict(conflict, payload.dayIdx)
        } else if (payload.start <= conflict.start) {
          // 新任务盖住旧任务头部：旧任务缩成 [新任务结束 + 1 分钟, 旧任务结束]
          const newStart = payload.end + 1
          if (newStart > conflict.end) {
            await this.deleteConflict(conflict, payload.dayIdx)
          } else {
            await this.shrinkConflict(conflict, payload.dayIdx, newStart, conflict.end)
          }
        } else {
          // 新任务盖住旧任务尾部 / 中间：旧任务缩成 [旧任务开始, 新任务开始 - 1 分钟]
          const newEnd = payload.start - 1
          if (newEnd < conflict.start) {
            await this.deleteConflict(conflict, payload.dayIdx)
          } else {
            await this.shrinkConflict(conflict, payload.dayIdx, conflict.start, newEnd)
          }
        }
      }
    },
    /** 冲突处理：删掉一条被完全盖住的旧记录 */
    async deleteConflict(conflict, dayIdx) {
      if (conflict.id) {
        try {
          const delApi = this.friendOwnerId
            ? () => deleteFriendTimeBlock(this.friendOwnerId, conflict.id)
            : () => deleteTimeBlock(conflict.id)
          const res = await delApi()
          if (!res.success || res.code !== 200) {
            console.warn('冲突删除后端记录失败', res.message)
            return
          }
        } catch (e) {
          console.error('冲突删除后端记录失败', e)
          return
        }
      }
      // ⚠️ 必须带上 key：父组件可能已经因为前面几次删除而让 index 失效
      this.$emit('delete-event', { dayIdx, index: conflict.index, key: conflict.key })
    },
    /** 冲突处理：把一条旧记录缩短到 [start, end] */
    async shrinkConflict(conflict, dayIdx, start, end) {
      if (conflict.id) {
        try {
          await this.updateConflictTimeBlock(conflict, dayIdx, start, end)
        } catch (e) {
          console.error('冲突缩短后端记录失败', e)
          return
        }
      }
      this.$emit('update-event', {
        dayIdx,
        index: conflict.index,
        key: conflict.key,
        start,
        end,
        cat: conflict.cat,
        text: conflict.text
      })
    },
    async updateConflictTimeBlock(conflict, dayIdx, start, end) {
      const day = this.days[dayIdx]
      if (!day) return
      const cat = this.catMap[conflict.cat]
      const startTime = this.buildDateTime(day.date, start)
      const endTime = this.buildDateTime(day.date, end)
      // 好友协同编辑场景走好友修改接口（带权限校验）
      const updApi = this.friendOwnerId
        ? (id, body) => updateFriendTimeBlock(this.friendOwnerId, id, body)
        : (id, body) => updateTimeBlock(id, body)
      const res = await updApi(conflict.id, {
        projectId: this.projectId,
        tagId: conflict.cat && conflict.cat !== 'none' ? conflict.cat : null,
        plannedStartTime: startTime,
        plannedEndTime: endTime,
        actualStartTime: startTime,
        actualEndTime: endTime,
        content: conflict.text || '',
        color: cat ? cat.color : null,
        note: ''
      })
      if (!res.success || res.code !== 200) {
        throw new Error(res.message || '缩短旧任务失败')
      }
    },
    buildDateTime(dateStr, minutes) {
      const [y, M, d] = dateStr.split('-').map(Number)
      return `${y}-${String(M).padStart(2, '0')}-${String(d).padStart(2, '0')}T${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:00`
    },
    emitPayload(payload) {
      if (typeof payload.index === 'number') {
        this.$emit('update-event', payload)
      } else {
        this.$emit('add-event', payload)
      }
    },
    /**
     * 只读表（好友时间表）专用：把鼠标进入的时间块连同它的屏幕坐标一起抛出去。
     * 父组件据此做「悬停 0.5s 才弹气泡 + 气泡贴在时间块右侧」。
     * 自己的可编辑表格不抛这两个事件，行为完全不变。
     */
    onBlockHoverEnter(dayIdx, index, e) {
      // 只读表（好友页）默认抛；可编辑表仅在 commentable=true（所有者看评论）时抛
      if (!this.readonly && !this.commentable) return
      const evt = this.events[dayIdx] && this.events[dayIdx][index]
      if (!evt) return
      this.$emit('hover-event', {
        dayIdx,
        index,
        event: { ...evt },
        rect: copyRect(e && e.currentTarget)
      })
    },
    onBlockHoverLeave() {
      if (!this.readonly && !this.commentable) return
      this.$emit('hover-leave')
    },
    onEditClick(dayIdx, index, key, e) {
      const i = this.findEventIndex(dayIdx, index, key)
      if (i < 0) return
      // 正在做：点击块直接「结束」，不进编辑弹窗（时间由系统记录）
      const evt = this.events[dayIdx][i]
      if (evt && evt.kind === 'running') {
        this.onEndRunning(dayIdx, index, key)
        return
      }
      if (this.readonly) {
        const readonlyEvt = this.events[dayIdx][i]
        if (readonlyEvt) {
          this.$emit('select-event', {
            dayIdx,
            index: i,
            event: { ...readonlyEvt },
            rect: copyRect(e && e.currentTarget)
          })
        }
        return
      }
      // 打开「修改记录」弹窗前先让父组件收起评论气泡，避免气泡悬在弹窗上
      if (this.commentable) this.$emit('hover-leave')
      this.editingEvent = { ...evt, index: i, key: evt.key }
      this.modalDayIdx = dayIdx
      this.modalRangeStart = 0
      this.modalRangeEnd = DAY_MINUTES
      this.modalVisible = true
    },
    async onDeleteClick(dayIdx, index, key) {
      try {
        await this.$confirm('删除后不可恢复，是否继续？', '删除确认', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
          confirmButtonClass: 'el-button--danger'
        })
      } catch (e) {
        return
      }

      // ⚠️ 按 key 重新定位：index 是渲染时记下来的，中途若删过记录它就已经失效了
      const i = this.findEventIndex(dayIdx, index, key)
      if (i < 0) {
        this.$message.warning('这条记录已经不存在了')
        return
      }
      const evt = this.events[dayIdx][i]
      if (evt && evt.id) {
        try {
          // 好友协同编辑场景走好友删除接口（带权限校验），其余走自己的接口
          const delApi = this.friendOwnerId
            ? () => deleteFriendTimeBlock(this.friendOwnerId, evt.id)
            : () => deleteTimeBlock(evt.id)
          const res = await delApi()
          if (!res.success || res.code !== 200) {
            console.warn('删除时间记录失败', res.message)
            this.$message.error(res.message || '删除失败')
            return
          }
        } catch (e) {
          console.error('删除时间记录失败', e)
          this.$message.error('删除失败，请稍后重试')
          return
        }
      }
      this.$emit('delete-event', { dayIdx, index: i, key: evt.key })
      this.$message.success('删除成功')
    },
    /**
     * 预设 → 勾选完成：调后端把状态置 COMPLETED（后端按计划区间回填 actual，成为普通时间块）。
     * 成功后把新的 actual 时间抛给父组件（ListPage）本地重新派生，立即可见。
     */
    async onCheckDone(dayIdx, index, key) {
      const i = this.findEventIndex(dayIdx, index, key)
      if (i < 0) return
      const evt = this.events[dayIdx][i]
      if (!evt.id) {
        this.$message.warning('这条记录还没有保存，无法完成')
        return
      }
      try {
        // 好友协同编辑场景：块的所有者是好友，必须走好友专用状态接口；
        // 调自己的 updateTimeBlockStatus 会因「不是所有者」直接失败。
        const res = this.friendOwnerId
          ? await updateFriendTimeBlockStatus(this.friendOwnerId, evt.id, 'COMPLETED')
          : await updateTimeBlockStatus(evt.id, 'COMPLETED')
        if (!res.success || res.code !== 200) {
          this.$message.error(res.message || '操作失败')
          return
        }
        this.$emit('update-status', {
          dayIdx,
          key,
          id: evt.id,
          kind: 'done',
          actualStartTime: evt.plannedStartTime,
          actualEndTime: evt.plannedEndTime
        })
        this.$message.success('已标记为完成')
      } catch (e) {
        console.error('勾选完成失败', e)
        this.$message.error('操作失败，请稍后重试')
      }
    },
    /**
     * 正在做 → 结束（含跨天拆块）。
     *
     * <p>同日结束：后端把 actualEnd 设为当前时刻；本地同步构造同一天的 actualEnd 让渲染立刻跟上。</p>
     * <p>跨天结束（用户 2026-10-13 要求：第二天结束也可以，但要拆成两块）：
     * 走 utils/runningEnd.js 的共享实现 —— 当天一段收到 23:59:59，之后的每一天各建一个块
     * （中间日是整天，最后一天到今天此刻），后端按 plannedStartTime 的日期分桶到各自的列。</p>
     */
    /** days 数组里某个日期（'yyyy-MM-dd'）对应的列下标；不在本周（例如下周）返回 -1 */
    dayIndexByDate(dateStr) {
      return this.days.findIndex(d => String(d.date) === dateStr)
    },
    async onEndRunning(dayIdx, index, key) {
      const i = this.findEventIndex(dayIdx, index, key)
      if (i < 0) return
      const evt = this.events[dayIdx][i]
      if (!evt.id) {
        this.$message.warning('这条记录还没有保存，无法结束')
        return
      }
      // 开始时刻：优先实际开始；老数据没有就退回计划开始；再没有就当今天
      let startDt = new Date(evt.actualStartTime || evt.plannedStartTime)
      if (isNaN(startDt.getTime())) startDt = new Date()
      const now = new Date()
      const sameDay = dateStrOf(startDt) === dateStrOf(now)
      // 跨了几天（≥2 才会出现整天块）
      let extraDays = 0
      if (!sameDay) {
        let c = new Date(startDt.getFullYear(), startDt.getMonth(), startDt.getDate() + 1)
        const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        while (c.getTime() <= today0.getTime()) {
          extraDays++
          c.setDate(c.getDate() + 1)
        }
      }
      try {
        await this.$confirm(
          sameDay
            ? '结束这个正在做的时间块？结束后将记录实际用时。'
            : `这个块开始于 ${dateStrOf(startDt)}，现在已经跨天了。\n` +
              `结束时会拆成 ${extraDays + 1} 块：当天一段，之后每天各一段（最后一段到今天 ${dateStrOf(now)}）。`,
          '结束确认', {
            confirmButtonText: '结束',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
      } catch (e) {
        return
      }
      let result
      try {
        result = await endRunningBlock({
          id: evt.id,
          block: evt,
          friendOwnerId: this.friendOwnerId
        })
      } catch (e) {
        console.error('结束失败', e)
        this.$message.error(e.message || '操作失败，请稍后重试')
        return
      }
      // 本地同步：开始的那块变成普通记录（跨天时结束 = 当天 23:59:59）
      this.$emit('update-status', {
        dayIdx,
        key,
        id: evt.id,
        kind: 'done',
        actualStartTime: isoLocal(startDt),
        actualEndTime: result.actualEndTime
      })
      // 跨天建出来的新块：目标日期在本周列里才补到本地（不在本周就翻过去自然能看到）
      const failed = result.created.filter(b => !b.id)
      result.created.forEach(b => {
        if (!b.id) return
        const idx = this.dayIndexByDate(b.dateStr)
        if (idx < 0) return
        this.$emit('add-event', {
          dayIdx: idx,
          start: b.startMin,
          end: b.endMin,
          cat: evt.cat,
          text: evt.text || '',
          id: b.id,
          kind: 'done',
          plannedStartTime: b.params.plannedStartTime,
          plannedEndTime: b.params.plannedEndTime,
          actualStartTime: b.params.actualStartTime,
          actualEndTime: b.params.actualEndTime
        })
      })
      if (failed.length) {
        this.$message.warning(`已结束，但有 ${failed.length} 天的跨天记录没建成功，请手动补录`)
      } else if (result.created.length) {
        this.$message.success(`已结束，并为之后 ${result.created.length} 天各记了一段`)
      } else {
        this.$message.success('已结束')
      }
    },
    toggleFullscreen() {
      const el = this.$refs.tablebox
      if (!document.fullscreenElement &&
          !document.webkitFullscreenElement &&
          !document.msFullscreenElement) {
        if (el.requestFullscreen) {
          el.requestFullscreen().catch(err => console.warn('全屏失败', err))
        } else if (el.webkitRequestFullscreen) {
          el.webkitRequestFullscreen()
        } else if (el.msRequestFullscreen) {
          el.msRequestFullscreen()
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(err => console.warn('退出全屏失败', err))
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
      }
    },
    downloadTable() {
      const data = {
        days: this.days,
        events: this.events,
        categories: this.categories,
        exportedAt: new Date().toISOString()
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `timetable-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
    },
    handleFullscreenChange() {
      const active = !!(document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement)
      this.isFullscreen = active
      document.body.style.overflow = active ? 'hidden' : ''
      // 进入/退出全屏后重新计算行高，让整张表铺满（或恢复）屏幕
      this.$nextTick(this.syncFullscreenRowHeight)
    },
    handleWindowResize() {
      this.syncFullscreenRowHeight()
      this.fitEventText()
    }
  },
  mounted() {
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('msfullscreenchange', this.handleFullscreenChange)
    window.addEventListener('resize', this.handleWindowResize)
    // 文字「放不下就整行隐藏」需要在渲染完成后量一次。
    // ⚠️ 行高变化（全屏 / 折叠展开）不会改变 .tablebox 自身的尺寸，只会让 <table> 变高，
    //    所以盯的是 <table> 而不是根容器，否则这些场景会漏量。
    if (typeof ResizeObserver !== 'undefined') {
      this.tableResizeObserver = new ResizeObserver(() => this.fitEventText())
      const table = this.$refs.tablebox && this.$refs.tablebox.querySelector('table')
      if (table) this.tableResizeObserver.observe(table)
    }
    this.$nextTick(this.fitEventText)
    // 周期刷新 todayKey / nowMinute，让跨天后「今天」列高亮自动跟上新的一天，
    // 同时让「在做 / 在做(记录) / 在做(计划)」徽标随真实时间推进（到点自动消失）。
    // 30s 一跳：块边界是分钟级，再密就是白烧渲染。
    this.tickTimer = setInterval(() => {
      this.todayKey = todayString()
      this.nowMinute = currentMinute()
    }, 30000)
  },
  updated() {
    // 数据 / 折叠状态变化都会重渲染时间块，渲染后必须重新量一遍文字是否放得下
    this.$nextTick(this.fitEventText)
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('msfullscreenchange', this.handleFullscreenChange)
    window.removeEventListener('resize', this.handleWindowResize)
    if (this.tableResizeObserver) {
      this.tableResizeObserver.disconnect()
      this.tableResizeObserver = null
    }
    document.body.style.overflow = ''
    if (this.tickTimer) {
      clearInterval(this.tickTimer)
      this.tickTimer = null
    }
  }
}
</script>

<style scoped>
.tablebox{
  overflow-y:auto;
  overflow-x:hidden;
  border:1px solid #558b2f;
  border-left:none;
  border-right:none;
  background:#fff;
  flex:1;
  min-height:420px;
  border-radius:0;
  position:relative;
  scrollbar-width:none;
  -ms-overflow-style:none;
  /* ⚠️ 不要在这里加 isolation:isolate / transform / filter / will-change 等会"撑出独立层叠上下文"的属性：
  *    添加记录的 QuickAddModal 是 position:fixed; z-index:1000，但它作为 .tablebox 的后代，
  *    一旦 .tablebox 自己成了层叠上下文，这块 z-index 就会被锁死在 .tablebox 内部，
  *    永远盖不过父级层叠上下文里的元素（顶部导航栏 .task-nav z-index:100、
  *    表格顶部白色 sticky 遮挡条 .panel.main::before z-index:40、以及同层靠后的 TimeStats），
  *    表现就是这些元素"浮在"添加记录悬浮窗之上、把弹窗遮住。
  *    去掉隔离后，模态框的 z-index:1000 能一路上浮到页面根层叠上下文（.frame），正确盖在最上层。
  *    表头 thead 是 position:sticky，本身自带层叠上下文（z-index:31），去掉 isolation 不影响其 sticky 行为。 */
}
.tablebox::-webkit-scrollbar{
  display:none;
}
.fullscreen-actions{
  position:absolute;
  top:0.75rem;
  right:0.75rem;
  z-index:20;
  display:none;
  align-items:center;
  gap:0.5rem;
}
.tablebox.fullscreen .fullscreen-actions,
.tablebox:fullscreen .fullscreen-actions,
.tablebox:-webkit-full-screen .fullscreen-actions,
.tablebox:-ms-fullscreen .fullscreen-actions{
  display:flex;
}
.fullscreen-actions .icon-btn{
  width:36px;
  height:36px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#f5f1d5;
  border:none;
  border-radius:50%;
  padding:0;
  color:#35512d;
  cursor:pointer;
  box-shadow:none;
  transition:background 0.15s ease, transform 0.1s ease;
}
.fullscreen-actions .icon-btn svg{
  width:20px;
  height:20px;
}
.fullscreen-actions .icon-btn:hover{
  background:#e7f5c8;
}
.fullscreen-actions .icon-btn:active{
  transform:translateY(2px);
}
.tablebox.fullscreen{
  position:fixed !important;
  top:0 !important;
  left:0 !important;
  width:100vw !important;
  height:100vh !important;
  max-width:none !important;
  max-height:none !important;
  z-index:9999 !important;
  border-radius:0 !important;
  border:none !important;
  overflow-y:auto !important;
  overflow-x:hidden !important;
  flex:none !important;
}
table{
  /* 关键：sticky 在 collapse 模式下表现不稳定；用 separate + spacing:0 视觉等同 collapse */
  border-collapse:separate;
  border-spacing:0;
  width:100%;
  table-layout:fixed;
}
th,td{
  /* 只在右侧和下侧画边框（每行/每列的右/下边界），配合 first-child 补左/上，
     这样相邻单元格不会各自画一遍边框 = 视觉等同 collapse 的 1px */
  border-right:1px solid #e2e8f0;
  border-bottom:1px solid #e2e8f0;
  padding:0;
  font-size:0.813rem;
}
th:first-child,td:first-child{
  border-left:1px solid #e2e8f0;
}
thead th{
  border-top:1px solid #e2e8f0;
}
/* 表头钉在滚动区顶部。
   注意：这依赖 .tablebox 是「真正的内部滚动容器」（有确定高度、会滚动）。
   若 .tablebox 撑到内容高度、改成整页滚动，sticky 相对不滚动的 .tablebox 就会失效，
   表头会跟着页面滚走 —— 所以 ListPage 的 .frame 必须用 height:100vh（不能用 min-height）。 */
thead{
  position:sticky;
  top:0;
  z-index:30;
}
th{
  background:#f7fafc;
  color:#558b2f;
  padding:0.75rem 0.25rem;
  font-weight:600;
  position:sticky;
  top:0;
  /* 高于 event-actions(4) / addbtn-gap(2) / event-block(3)，低于 .fullscreen-actions(20) 之外的全屏按钮 */
  z-index:31;
  /* 滚动时给表头一个淡淡的阴影，和下方行形成视觉分隔 */
  box-shadow:0 2px 6px rgba(85,139,47,0.08);
}
th.timecol{
  width:120px;
}
.daydate{
  display:block;
  font-weight:400;
  font-size:0.688rem;
  opacity:.75;
  margin-top:0.125rem;
  color:#718096;
}

/* 今天列强调：表头底色 + 徽章 + 整列淡绿底 */
th.is-today{
  background:#eaf3de;
  color:#27500a;
}
th.is-today .daydate{
  color:#3b6d11;
  opacity:.85;
}
.today-badge{
  display:inline-block;
  margin-left:0.25rem;
  padding:0.063rem 0.25rem;
  border-radius:0.5rem;
  background:#97c459;
  color:#fff;
  font-size:0.563rem;
  font-weight:600;
  line-height:1.2;
  vertical-align:middle;
}
td.datacell.is-today{
  background:#f4f9ec;
}
td.timecell{
  background:#f7fafc;
  text-align:center;
  color:#4a5568;
  font-size:0.75rem;
  width:120px;
  white-space:nowrap;
  user-select:none;
}
td.datacell{
  border-top:none;
  border-bottom:none;
  border-left:none;
  border-right:none;
  height:42px;
  vertical-align:top;
  position:relative;
}
.cellinner{
  position:relative;
  width:100%;
  height:100%;
  min-height:42px;
  overflow:visible;
}
.event-block{
  position:absolute;
  left:0;
  right:0;
  border-radius:0;
  padding:0.125rem 0.25rem;
  font-size:0.625rem;
  line-height:1.2;
  overflow:hidden;
  cursor:pointer;
  box-shadow:0 1px 2px rgba(0,0,0,0.08);
  border:2px solid rgba(0,0,0,0.18);
  z-index:3;
  /* 让绝对定位的子元素能参与 flex 计算，避免被 padding 挤出 */
  display:flex;
  flex-direction:column;
  min-height:0;
}
.event-block:hover{
  filter:brightness(0.95);
}
.event-actions{
  position:absolute;
  top:0.125rem;
  right:0.125rem;
  display:flex;
  gap:0.125rem;
  opacity:0;
  transition:opacity .15s;
  z-index:4;
}
.event-block:hover .event-actions{
  opacity:1;
}
.event-actions >>> .el-button{
  padding:0;
  font-size:0.75rem;
  color:#2d3748;
}
.event-actions >>> .el-button:hover{
  color:#558b2f;
}
.event-text{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  text-align:center;
  width:100%;
  height:100%;
  min-height:0;
  overflow:hidden;
  /* 行间不留额外空隙：一个 1 小时格只有 38px 可用高，三行（分类/事项/时间）
     加上行距就放不下了 —— 放不下就会有行被折走、在原位留下「空行」。
     行高本身（1.05）已经留了 5% 的行距，这里再叠 gap 纯属浪费高度。 */
  gap:0;
  /* 时间块文字使用常规字重；颜色由 sliceStyle 的 color 决定 */
  font-weight:400;
}
.event-title{
  font-size:0.625rem;
  /* 行高 1.05（原 1.2）：三行合计必须塞进 1 小时格的 38px 可用高，
     否则最后一行会被折到框外、原位留下空行。1.05 仍留了 5% 行距，不会切字。 */
  line-height:1.05;
  max-width:100%;
  min-width:0;
  width:100%;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
.event-time{
  font-size:0.563rem;
  line-height:1.05;
  opacity:0.9;
  max-width:100%;
  min-width:0;
  width:100%;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
/* 时间块里的「分类图标 + 名称」：只在没填事项时显示，排在最前，一眼能看出是哪类。
   图标复用 Font Awesome 的 web-font class（与图例里一致），无图标时退化成小色点。 */
.event-cat{
  display:flex;
  align-items:center;
  justify-content:center;
  gap:0.1875rem;
  max-width:100%;
  min-width:0;
  line-height:1.05;
  font-size:0.6875rem;
}
.event-cat i{
  font-size:0.6875rem;
  line-height:1;
}
.event-cat-emoji{
  font-size:0.6875rem;
  line-height:1;
}
.event-cat-dot{
  width:7px;
  height:7px;
  border-radius:2px;
  flex:0 0 auto;
}
.event-cat-name{
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  max-width:100%;
  min-width:0;
}
/* 极小块（< 15 分钟，< 12.6px 高）：缩小标题字号，并去掉内边距避免文字被挤出 */
.event-block.is-tiny{
  padding:0 0.25rem;
}
.event-block.is-tiny .event-text{
  flex-direction:row;
  gap:0.1875rem;
  white-space:nowrap;
}
.event-block.is-tiny .event-cat,
.event-block.is-tiny .event-cat i,
.event-block.is-tiny .event-cat-emoji,
.event-block.is-tiny .event-cat-name,
.event-block.is-tiny .event-title{
  font-size:0.5rem;
  line-height:1;
}
.event-block.is-tiny .event-cat{
  flex:0 1 auto;
}
.event-block.is-tiny .event-title{
  flex:1 1 0;
  width:auto;
}
/* 微块（< ~9 分钟，< 6.3px 高）：文字完全不可读，直接隐藏，只留纯色条；hover 看 tooltip 即可 */
.event-block.is-micro{
  padding:0;
  border:0;
  box-shadow:none;
  cursor:pointer;
}
.event-block.is-micro .event-text{
  display:none;
}
.addbtn-gap{
  position:absolute;
  left:0;
  right:0;
  border:1px dashed #7cb342;
  border-radius:0;
  background:rgba(124, 179, 66, 0.08);
  color:#7cb342;
  font-size:1.125rem;
  line-height:1;
  cursor:pointer;
  opacity:0.35;
  transition:opacity .15s, background .15s, color .15s;
  z-index:2;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:0;
  overflow:hidden;
}
.addbtn-gap:hover{
  opacity:1;
  background:#dcedc8;
  color:#558b2f;
}
.addbtn-icon{
  pointer-events:none;
}
tbody tr:nth-child(even) td.timecell{
  background:#f1f8e9;
}
.fold-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:14px;
  height:14px;
  margin-right:0.375rem;
  color:#558b2f;
  cursor:pointer;
  font-size:0.625rem;
  line-height:1;
  vertical-align:middle;
  user-select:none;
}
.fold-btn:hover{
  color:#1a6fc4;
}
.collapsed-row{
  cursor:pointer;
  background:#f7fafc;
}
.collapsed-row:hover{
  background:#f1f8e9;
}
.collapsed-row td{
  padding:0.5rem 0.75rem;
  color:#718096;
  font-size:0.75rem;
  border:1px solid #e2e8f0;
}
.fold-hint{
  display:inline-flex;
  align-items:center;
  gap:0.375rem;
}
.fold-icon{
  color:#558b2f;
  font-size:0.625rem;
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影。
   事件块与空位块的 padding、top、height、字号等尺寸全部沿用上面的原值，未做任何改动。 */
.tablebox{
  /* ⚠️ 外框的边框**不画在 .tablebox 上**，四条边全部交给单元格绘制
     （见下方 `th:first-child / td:last-child / thead th / tbody tr:last-child td` 那组规则）。
     原因：表头是 position:sticky 的。只要左右竖线画在容器上，表头钉住时
     它旁边就永远是**直线**，而表头自己的角是**弧线** ——
     "直角竖线 + 内缩弧线"并存，怎么调圆角都救不回来。
     这里只保留圆角背景，配合单元格的圆角让白底在四个角上也被裁圆。 */
  border:none;
  border-radius:0.75rem;
  background:var(--lp-surface,#fff);
}
.fullscreen-actions .icon-btn{
  background:transparent;
  border-radius:0.75rem;
  color:var(--lp-text-3,#94a59b);
  transition:background 0.15s ease, color 0.15s ease, transform 0.1s ease;
}
.fullscreen-actions .icon-btn:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.fullscreen-actions .icon-btn:active{
  transform:translateY(1px);
}
/* 去掉竖向网格线，同时去掉表体内部的横向分隔线（表头那条分隔线保留，
   sticky 时靠它区分表头与内容） */
th,td{
  border-right:none;
  border-bottom:none;
}
th{
  background:var(--lp-surface,#fff);
  color:var(--lp-text-2,#5f7268);
  border-bottom:1px solid var(--lp-border,#e3efe6);
  box-shadow:none;
}
.daydate{
  color:var(--lp-text-3,#94a59b);
}
/* 今天列：表头单元格用浅绿底 + 主色文字 + 底部一道强调线，明显区别于其它表头单元格；
   整列数据格不再整列高亮（只在表头提示"今天"），避免大面积染色干扰阅读 */
th.is-today{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
  box-shadow:inset 0 -2px 0 0 var(--lp-primary,#4aa46a);
}
th.is-today .daydate{
  color:var(--lp-primary-dark,#2f7a4a);
  opacity:.85;
}
.today-badge{
  border-radius:0.625rem;
  background:var(--lp-primary,#4aa46a);
}
td.datacell.is-today{
  background:transparent;
}
/* ===== 时间表外框：四条边 + 四角圆角，全部由单元格绘制 =====
   为什么不用 .tablebox 画边框：表头是 sticky 的，只要左右竖线画在容器上，
   表头钉住时它旁边就永远是**直线**，而表头自己的角是**弧线** ——
   "直角竖线 + 内缩弧线"并存，怎么调圆角都救不回来。
   交给单元格后，表头的边框会带着圆角一起钉住，任何滚动位置形状都自洽。 */
/* 带 .tablebox 前缀是为了压过 `td.datacell{ border:none }`（(0,2,1)），
   否则表体最后一列的右边框会被它清掉。 */
.tablebox th:first-child,
.tablebox td:first-child{
  border-left:2px solid var(--lp-border-strong,#a5cdb3);
}
.tablebox th:last-child,
.tablebox td:last-child{
  border-right:2px solid var(--lp-border-strong,#a5cdb3);
}
.tablebox thead th{
  border-top:2px solid var(--lp-border-strong,#a5cdb3);
}
.tablebox tbody tr:last-child td{
  border-bottom:2px solid var(--lp-border-strong,#a5cdb3);
}
.tablebox thead th:first-child{
  border-top-left-radius:0.75rem;
}
.tablebox thead th:last-child{
  border-top-right-radius:0.75rem;
}
.tablebox tbody tr:last-child td:first-child{
  border-bottom-left-radius:0.75rem;
}
.tablebox tbody tr:last-child td:last-child{
  border-bottom-right-radius:0.75rem;
}
td.timecell{
  background:var(--lp-surface,#fff);
  color:var(--lp-text-3,#94a59b);
}
tbody tr:nth-child(even) td.timecell{
  background:var(--lp-surface-2,#f7fbf8);
}
/* 时间块：2px 描边 + 直角（去掉圆角，避免同排相邻两列交界处留出小缺口），去掉投影。
   padding 仍是 0.125rem 0.25rem，未改动。
   描边颜色由 sliceStyle 按分类色（p.base）内联设置；这里只定宽度。
   背景用 categoryPalette 的 blockBg（已兑淡一档），边框更明显、易区分。 */
.event-block{
  border-radius:0;
  border:2px solid rgba(0,0,0,.18);
  box-shadow:none;
}
.event-block:hover{
  filter:brightness(0.97);
}
.event-actions >>> .el-button:hover{
  color:var(--lp-primary-dark,#2f7a4a);
}
/* 空位块与时间块保持同样的弧度 */
.addbtn-gap{
  border-color:var(--lp-border,#e3efe6);
  border-radius:0.25rem;
  background:transparent;
  color:var(--lp-primary,#4aa46a);
}
.addbtn-gap:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary-soft2,#d5ebdb);
  color:var(--lp-primary-dark,#2f7a4a);
}
.fold-btn{
  color:var(--lp-primary,#4aa46a);
}
.fold-btn:hover{
  color:var(--lp-primary-dark,#2f7a4a);
}
.fold-icon{
  color:var(--lp-primary,#4aa46a);
}
.collapsed-row{
  background:var(--lp-surface-2,#f7fbf8);
}
.collapsed-row:hover{
  background:var(--lp-primary-soft,#e9f5ec);
}
.collapsed-row td{
  border:none;
  color:var(--lp-text-2,#5f7268);
}

/* ===== 全屏展示：整张时间表拉伸铺满屏幕 =====
   平时行高固定 42px；进入全屏后由 JS 按「可视高度 ÷ 行数」算出 --tt-row-h 并写在 .tablebox 上，
   再由 `table{height:100%}` 把 floor 取整剩下的零头分摊掉 —— 上下左右都顶到屏幕边缘、无需滚动。
   退出全屏时 JS 移除该变量，自动回落到 42px。 */
.tablebox{
  --tt-radius:0.75rem;
  --tt-row-h:42px;
  border-radius:var(--tt-radius);
}
td.datacell{
  height:var(--tt-row-h,42px);
}
.cellinner{
  min-height:var(--tt-row-h,42px);
}
/* 全屏时顶到屏幕边缘，四角不再留圆角 */
.tablebox.fullscreen{
  --tt-radius:0;
}
.tablebox:fullscreen{
  --tt-radius:0;
}
.tablebox.fullscreen table{
  height:100%;
}
.tablebox:fullscreen table{
  height:100%;
}
/* 折叠行不参与高度均分，去掉纵向内边距让它保持紧凑 */
.tablebox.fullscreen .collapsed-row td{
  padding-top:0;
  padding-bottom:0;
}
.tablebox:fullscreen .collapsed-row td{
  padding-top:0;
  padding-bottom:0;
}
.tablebox thead th:first-child{
  border-top-left-radius:var(--tt-radius,0.75rem);
}
.tablebox thead th:last-child{
  border-top-right-radius:var(--tt-radius,0.75rem);
}
.tablebox tbody tr:last-child td:first-child{
  border-bottom-left-radius:var(--tt-radius,0.75rem);
}
.tablebox tbody tr:last-child td:last-child{
  border-bottom-right-radius:var(--tt-radius,0.75rem);
}

/* ===== 评论气泡指向的那个时间块 =====
   气泡贴左还是贴右取决于「哪边放得下」，用户没法预判；所以靠**高亮块本身**来建立对应关系，
   比让用户去找气泡在哪一侧可靠得多。
   用 outline 而不是 border：border 会挤掉块的 1px 描边并改变尺寸（块是绝对定位 + 百分比高度，
   border 变化会让内容轻微位移），outline 画在盒外、完全不占布局。
   z-index 抬到 6：高于普通块(3) 与块内操作按钮(4)，保证高亮圈不被相邻块压住。 */
.event-block.is-bubble-source{
  outline:2px solid var(--lp-primary-dark,#2f7a4a);
  outline-offset:1px;
  z-index:6;
  box-shadow:0 0 0 3px rgba(47,122,74,.18);
}

/* ===== 时间块文字：分类优先，从顶部排，放不下的折叠到下方、块内滚动查看 =====
   需求：「时间块的文字显示，优先显示分类标题，如果一行文字显示不完全，则隐藏掉」
   + 用户补充：内边距再小一些；优先显示最上面的一行；显示不完的折叠起来滑动事件块查看。

   - 竖向内边距压到 0：一个 1 小时行只有 42px 高，扣掉 2px 描边 ×2 后文字可用高 38px，
     把内边距也让给文字（横向内边距保留，避免文字贴到左右描边上）。
   - 行元素一律 `flex:0 0 auto`：保持**自然行高**不被压缩。
     原来默认的 `flex-shrink:1` 会把行压扁（行内文字被切一半），
     正是「文字显示不完全」的根因。
   - 分类行（`.event-cat` / 无图标时的 `.event-cat-name`）优先级最高，永不因为空间不足被隐藏；
   - `.is-scroll`（由 fitEventText() 判定纵向放不下后挂上）：文字改为**从顶部排**，
     并把放不下的行折到可视区下方，块内可上下滑动完整查看 —— 不切字、也不丢内容；
   - 横向放不下的行（事项 / 时间）由 fitEventText() 打上 `.is-overflow` 整行隐藏。 */
.event-block{
  padding-top:0;
  padding-bottom:0;
}
.event-text > *{
  flex:0 0 auto;
}
.event-text > .is-overflow{
  display:none;
}
/* 内容超出块高：从顶部开始排（保住最上面那行），其余折到下方，块内仍可滚动查看。
   滚动条**按要求隐藏**（用户：事件块的滚轴隐藏掉）—— 只隐藏滚动条，滚动能力保留，
   滚轮 / 触摸板 / 拖拽依然能滚到被折下去的那几行。
   ⚠️ `scrollbar-width:none` 与 `::-webkit-scrollbar{display:none}` 要**同时写**：
   前者管 Firefox / 新版 Chrome，后者管 WebKit（Safari / 旧 Chromium）。
   只写一个在另一类内核上会露出默认滚动条。 */
.event-text.is-scroll{
  justify-content:flex-start;
  overflow-y:auto;
  scrollbar-width:none;
  -ms-overflow-style:none;
}
.event-text.is-scroll::-webkit-scrollbar{
  width:0;
  height:0;
  display:none;
}
/* 分类行连自然字号都放不下时的紧凑字号：宁可小一号，也不显示被切一半的分类名
   （由 fitEventText() 按需给 .event-block 挂 is-cat-compact，量准了才加） */
.event-block.is-cat-compact .event-cat,
.event-block.is-cat-compact .event-cat i,
.event-block.is-cat-compact .event-cat-emoji,
.event-block.is-cat-compact .event-cat-name{
  font-size:0.5rem;
  line-height:1;
}
/* 极小块（<25%）原本是「分类 + 事项」横排一行：分类不许被挤，让事项让位
   （事项被挤到放不下时由 fitEventText() 整行隐藏，而不是显示 "学…"） */
.event-block.is-tiny .event-cat{
  flex:0 0 auto;
}
.event-block.is-tiny .event-title{
  flex:0 1 auto;
  min-width:0;
}

/* ===== 时间块右下角的互动角标（评论 / 点赞） =====
   需求：「如果有评论或者点赞，请在时间块的右下角显示图标和数字，方便用户判断这里有没有与好友的交互」。
   - 绝对定位贴右下角，**不参与 .event-block 的 flex 布局**，所以不会挤动块内文字（块内尺寸一律不动）；
   - `pointer-events:none`：角标纯展示，不拦截时间块的 hover / click（悬停评论气泡、点击改记录都不受影响）；
   - **没有底色**（用户明确要求「不要背景颜色」）：图标与数字直接压在时间块上，
     颜色由 is-comment / is-like 决定 —— **评论（气泡图标）绿 `#4aa46a`、点赞（心形）红 `#d9534f`**，
     两种都是项目已有色（App.vue 默认主色 / 现有红），**写死而不走 `--lp-*` 令牌**，
     否则会跟着工作表主题变色（见下方 is-comment 处的说明）；
   - 极小块（is-tiny，<25%，约 15 分钟）只有约 10px 高：收紧间距即可（本来就没底色）；
   - 微块（is-micro，<15%）连文字都不显示，角标在模板里已按 isMicroBlock 过滤掉。 */
.event-badges{
  position:absolute;
  right:0.1875rem;
  bottom:0.0625rem;
  display:flex;
  align-items:center;
  gap:0.25rem;
  pointer-events:none;
  z-index:5;
}
.event-badge{
  display:inline-flex;
  align-items:center;
  gap:0.0625rem;
  color:var(--lp-text,#2d3748);
  font-size:0.5rem;
  line-height:1.1;
}
.event-badge i{
  font-size:0.5rem;
  line-height:1;
}
/* 数字跟随所在角标的颜色（继承 .event-badge 的 color），所以这里只定色、不用单独设。
   ⚠️ 评论角标写死绿色 `#4aa46a`，**不要**换成 `var(--lp-primary)`：
   `--lp-primary` 会被 ListPage 的 themeStyle 按工作表主色覆盖（蓝色主题的工作表会把角标染成蓝色），
   而用户要的是「气泡固定绿色」。点赞红同理写死。 */
.event-badge.is-comment{
  color:#4aa46a;
}
.event-badge.is-like{
  color:#d9534f;
}
.event-badge-num{
  font-variant-numeric:tabular-nums;
}
.event-block.is-tiny .event-badges{
  right:0.125rem;
  bottom:0;
  gap:0.1875rem;
}

/* ===== 时间块状态角标：计划 / 在做 =====
   贴左上角，纯展示（pointer-events:none），不参与点击与 flex 布局。
   文字小号、半透明白底，保证压在任意分类色上都清晰。 */
.block-status{
  position:absolute;
  top:0.125rem;
  left:0.25rem;
  z-index:5;
  font-size:0.5rem;
  line-height:1.1;
  padding:0.0625rem 0.25rem;
  border-radius:0.375rem;
  font-weight:600;
  letter-spacing:0.02em;
  pointer-events:none;
  user-select:none;
  white-space:nowrap;
}
.block-status.is-planned{
  background:rgba(255,255,255,0.75);
  color:#4a5568;
}
.block-status.is-running{
  background:rgba(255,255,255,0.85);
  color:#2f7a4a;
}

/* ===== 「在做」的「结束（暂停）」：文字下方 + 整块垂直居中 + 无底色大号灰图标 =====
   不再塞进右上角的 .event-actions（那里是次要操作位，且「结束」挤在角上又小又容易和 × 混淆），
   而是作为 .event-block 的 flex 子项排在 .event-text 之后。
   块上加 `justify-content:center`，让「文字 + 按钮」**这一组整体垂直居中**：
   按钮因此落在块的竖向中间（而不是贴底），同时仍在文字下方，不会压住文字。 */
.event-block.is-running{
  justify-content:center;
}
.event-block.is-running .event-text{
  /* 原为 height:100%（独占整块高度）→ 会把按钮顶到块底。
     改回「按内容高度」（0 1 auto = 可压缩），剩下的空间由块的 justify-content:center
     上下对半分，文字与按钮作为一组居中。fitEventText 量的是 .event-text 的 clientHeight，
     空间不够时它会自动折掉放不下的行，不会溢出。 */
  flex:0 1 auto;
  height:auto;
}
.running-end{
  flex:0 0 auto;
  align-self:center;
  width:1.5rem;
  height:1.5rem;
  margin:0.1875rem 0 0;
  padding:0;
  display:flex;
  align-items:center;
  justify-content:center;
  border:none;
  border-radius:50%;
  /* 无底色：按钮本身就是一枚图标，白底圆钮在块里太抢眼（用户要求去掉） */
  background:transparent;
  box-shadow:none;
  /* 灰色：块内是分类彩底，「结束」是中性操作，不再用主题绿抢分类色的注意力 */
  color:#4b5563;
  cursor:pointer;
  transition:color .15s, transform .08s;
}
.running-end i{
  /* 放大：0.875rem → 1.375rem（约 15.8px → 24.8px），去掉底色后靠字号撑起存在感。
     fa-circle-pause 自带外圈留白，视觉上比同字号的 fa-pause 显小，故给到 1.375rem。
     另加一层极淡白描边，保证压在深分类色上也看得清。 */
  font-size:1.375rem;
  line-height:1;
  text-shadow:0 1px 2px rgba(255,255,255,0.7);
}
.running-end:hover{
  color:#2d3748;
  transform:scale(1.08);
}
/* 太矮的块连文字都要折叠，放不下这枚按钮 —— 不显示（点块本身同样能结束） */
.event-block.is-tiny .running-end{
  display:none;
}

/* ===== 「在做」右上角的「删除 ×」：常驻可见、无白底、图标换成 Font Awesome =====
   之前给 .event-actions 里的按钮统一套了白底胶囊，那是为让「结束」在渐变彩底上看得清；
   「结束」已挪走，× 就恢复成普通文字按钮 —— 角上一枚白胶囊太抢眼。
   为在彩底上仍然清晰，只给一层极淡的白色**文字阴影**（不是背景色）。 */
.event-block.is-running .event-actions{
  opacity:1;
}
.event-block.is-running .event-actions >>> .el-button{
  padding:0;
  color:#2d3748;
  background:transparent;
  box-shadow:none;
  text-shadow:0 1px 2px rgba(255,255,255,0.65);
}
.event-block.is-running .event-actions >>> .el-button:hover{
  color:var(--lp-primary-dark,#2f7a4a);
}
/* 操作区图标统一走 Font Awesome（与块内分类图标同一套）。
   Element 的 el-icon-* 字形本身偏大，FA 同字号会显小，这里补到 0.875rem 对齐视觉重量。 */
.event-actions >>> .el-button i{
  font-size:0.875rem;
  line-height:1;
}

/* ===== 「计划」的「已完成」：与「在做」的「结束（暂停）」同一套位置与样式 =====
   同样从右上角 .event-actions 挪出来，作为 .event-block 的 flex 子项排在 .event-text 之后；
   块上加 `justify-content:center`，让「文字 + 按钮」这一组整体垂直居中 ——
   按钮因此落在块的**竖向中间**（而不是贴底），同时仍在文字下方，不会压住文字。
   ⚠️ 「已完成」挪走后，.event-actions 里只剩「删除 ×」，保持原来的 hover 显隐即可。 */
.event-block.is-planned{
  justify-content:center;
}
.event-block.is-planned .event-text{
  /* 同 .event-block.is-running .event-text：改回「按内容高度」，
     剩下的空间由块的 justify-content:center 上下对半分。 */
  flex:0 1 auto;
  height:auto;
}
.planned-done{
  flex:0 0 auto;
  align-self:center;
  width:1.5rem;
  height:1.5rem;
  margin:0.1875rem 0 0;
  padding:0;
  display:flex;
  align-items:center;
  justify-content:center;
  border:none;
  border-radius:50%;
  /* 无底色 / 灰色：块内是分类彩底，中性操作不抢分类色的注意力 */
  background:transparent;
  box-shadow:none;
  color:#4b5563;
  cursor:pointer;
  transition:color .15s, transform .08s;
}
.planned-done i{
  /* 与 .running-end i 同字号：fa-circle-check 同样自带外圈留白，1.375rem 才和它视觉等重。
     极淡白描边保证压在深分类色上也看得清。 */
  font-size:1.375rem;
  line-height:1;
  text-shadow:0 1px 2px rgba(255,255,255,0.7);
}
.planned-done:hover{
  color:var(--lp-primary-dark,#2f7a4a);
  transform:scale(1.08);
}
/* 太矮的块连文字都要折叠，放不下这枚按钮 —— 不显示（点块本身可进编辑弹窗改状态） */
.event-block.is-tiny .planned-done{
  display:none;
}
</style>
